import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSMLSettingsTabs } from '../tabs/useSMLSettingsTabs';
import { SMLSettingsTabDef, SMLSettingsTabId } from '../tabs/types';

const TAB_QUERY_PARAM = 'tab';

function isTabId(value: string | null): value is SMLSettingsTabId {
  return value !== null && (Object.values(SMLSettingsTabId) as string[]).includes(value);
}

export interface SettingsTabSelection {
  tabs: SMLSettingsTabDef[];
  /** The tab that `?tab=` names, or null (the phone shows the index then). */
  requestedTab: SMLSettingsTabId | null;
  /** The tab the pane shows from md up: the requested tab, else the first one. */
  activeTab: SMLSettingsTabId;
  selectFromRail: (tabId: SMLSettingsTabId) => void;
  selectFromIndex: (tabId: SMLSettingsTabId) => void;
  backToIndex: () => void;
}

/**
 * The open Settings tab, kept in `?tab=`. The page head and the pane read the same value.
 */
export const useSettingsTabSelection = (): SettingsTabSelection => {
  const tabs = useSMLSettingsTabs();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = useMemo<SMLSettingsTabId | null>(() => {
    const raw = searchParams.get(TAB_QUERY_PARAM);
    return isTabId(raw) && tabs.some((t) => t.id === raw) ? raw : null;
  }, [searchParams, tabs]);
  const activeTab = requestedTab ?? tabs[0]?.id ?? SMLSettingsTabId.account;

  const selectTab = useCallback(
    (tabId: SMLSettingsTabId, replace: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(TAB_QUERY_PARAM, tabId);
          return next;
        },
        { replace }
      );
    },
    [setSearchParams]
  );
  const selectFromRail = useCallback((tabId: SMLSettingsTabId) => selectTab(tabId, true), [selectTab]);
  const selectFromIndex = useCallback((tabId: SMLSettingsTabId) => selectTab(tabId, false), [selectTab]);
  const backToIndex = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(TAB_QUERY_PARAM);
      return next;
    });
  }, [setSearchParams]);

  return { tabs, requestedTab, activeTab, selectFromRail, selectFromIndex, backToIndex };
};
